import React, { useEffect, useMemo, useRef, useState } from "react";
import { getCoachPreview, saveCoachPreview } from "../../services/authService";
import {
	modeleVide,
	modeleDepuisFiche,
	appliquerBrouillon,
	corpsToutCoach,
	seriesSansConsigne,
	modeleADesValeurs,
	champsInvalides,
	texteOuNull,
} from "../../utils/ficheConsignes";
import { formatWeight } from "../../utils/weight";
import useAlerteDepart from "../../utils/useAlerteDepart";

// Panneau « Consignes et objectifs » : la fiche Gym'Coach d'une séance de
// l'élève, éditable sur ses trois niveaux (résumé, texte d'exercice, séries).
// Tout ce qui est enregistré d'ici est signé coach (D8) et prime sur les
// propositions GymNote, y compris après un recalcul par l'élève.
//
// Il ne touche pas aux reps/poids de l'éditeur de séance : il lit la séance
// pour ses exercices et le nombre de séries, rien d'autre.

const formaterDate = (ts) => {
	if (!ts) return "";
	const d = new Date(Number(ts));
	return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR");
};

const TEXTES_RAISON = {
	hors_ligne: "Connexion impossible. Vérifiez le réseau et réessayez.",
	non_authentifie: "Session expirée : reconnectez-vous.",
	eleve_non_lie: "Cet élève n'est pas lié à votre compte.",
	seance_inconnue: "Séance introuvable : elle a peut-être été supprimée.",
	conflit: "La fiche a été modifiée entre-temps. Rechargez-la avant d'enregistrer.",
	abonnement_requis: "Gym'Coach est compris dans GymNote+ et GymNote Pro, à souscrire depuis l'app mobile GymNote (App Store ou Google Play).",
	abonnement_requis_coach: "La proposition GymNote est comprise dans GymNote Pro, à souscrire depuis l'app mobile GymNote (App Store ou Google Play). En attendant, écrivez vos consignes vous-même.",
	indisponible: "Le service Gym'Coach ne répond pas pour le moment.",
};
const texteRaison = (raison) => TEXTES_RAISON[raison] || TEXTES_RAISON.indisponible;

const pluriel = (n, un, plusieurs) => (n > 1 ? plusieurs : un);

const MESSAGE_CHAMPS_INVALIDES = "Corrigez les champs en rouge avant d'enregistrer.";

// Attributs d'un champ chiffré signalé invalide : bordure rouge, lu comme
// invalide par les lecteurs d'écran et relié à l'aide affichée sous la série.
const attributsInvalide = (invalide, idAide) =>
	invalide ? { className: "consignes-invalide", "aria-invalid": "true", "aria-describedby": idAide } : {};

// estSoiMeme : le coach ouvre sa propre séance ; personne d'autre à prévenir.
export default function ConsignesPanel({ studentId, training, onClose, estSoiMeme = false }) {
	const [modele, setModele] = useState(null);
	const [chargement, setChargement] = useState(true);
	const [remplissage, setRemplissage] = useState(false);
	const [enregistrement, setEnregistrement] = useState(false);
	const [erreur, setErreur] = useState(null);
	const [message, setMessage] = useState(null);
	// Des champs modifiés depuis le dernier chargement/enregistrement : une
	// fermeture doit alors demander confirmation, et quitter la page aussi.
	const [modifie, setModifie] = useState(false);
	useAlerteDepart(modifie);
	// Le dernier enregistrement a échoué sur un conflit de version : propose
	// de recharger plutôt que de réessayer à l'aveugle.
	const [conflit, setConflit] = useState(false);

	// Les champs chiffrés illisibles, repérés par "idExercice:ordre:champ" :
	// bordure rouge, aide sous la série, enregistrement refusé.
	const invalides = useMemo(() => (modele ? champsInvalides(modele) : []), [modele]);
	const clesInvalides = useMemo(
		() => new Set(invalides.map((c) => `${c.idExercice}:${c.ordre}:${c.champ}`)),
		[invalides]
	);
	const estInvalide = (idExercice, ordre, champ) => clesInvalides.has(`${idExercice}:${ordre}:${champ}`);

	// Recharge la fiche depuis l'API. Après un enregistrement, avec
	// { garderMessage: true } : le message de succès survit au rechargement et
	// le formulaire reste affiché (désactivé par l'enregistrement en cours)
	// au lieu de céder la place à « Chargement… » : le coach garde sa place.
	const charger = async ({ garderMessage = false } = {}) => {
		if (!garderMessage) {
			setChargement(true);
			setMessage(null);
		}
		setErreur(null);
		setConflit(false);
		const res = await getCoachPreview(studentId, training.id);
		if (res.success) {
			setModele(modeleDepuisFiche(res.fiche, training));
			setModifie(false);
		}
		// « Rien à dire » n'est pas une panne : le panneau s'ouvre vide, une
		// ligne par série, et le coach écrit.
		else if (res.raison === "rien_a_dire") {
			setModele(modeleVide(training));
			setModifie(false);
		}
		// Sans droit à Gym'Coach : pas de proposition GymNote, mais le coach
		// écrit toujours ses consignes à la main (spec 2026-10-07, § 4.3).
		else if (res.raison === "abonnement_requis_coach" || res.raison === "abonnement_requis") {
			setModele(modeleVide(training));
			setModifie(false);
			setMessage(texteRaison(res.raison));
		} else {
			// Les saisies ont quitté l'écran avec le modèle : plus rien à perdre
			// à la fermeture.
			setModele(null);
			setModifie(false);
			setErreur(texteRaison(res.raison));
		}
		setChargement(false);
	};

	useEffect(() => {
		charger();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [studentId, training.id]);

	const preremplirAvecGymCoach = async () => {
		if (remplissage || enregistrement || !modele) return;
		if (modeleADesValeurs(modele) && !window.confirm("Remplacer les champs actuels par la proposition Gym'Coach ?")) return;
		setRemplissage(true);
		setErreur(null);
		setMessage(null);
		const res = await getCoachPreview(studentId, training.id, { brouillon: true });
		setRemplissage(false);
		if (!res.success) {
			setErreur(
				res.raison === "rien_a_dire"
					? "Gym'Coach n'a rien à proposer sur cette séance : pas encore d'historique sur ces exercices."
					: texteRaison(res.raison)
			);
			return;
		}
		setModele((m) => appliquerBrouillon(m, res.fiche));
		setModifie(true);
		setMessage("Champs préremplis par Gym'Coach. Relisez, ajustez, puis enregistrez.");
	};

	const enregistrer = async () => {
		if (enregistrement || remplissage || !modele) return;
		if (invalides.length > 0) {
			setErreur(MESSAGE_CHAMPS_INVALIDES);
			return;
		}
		// Tout ce qui part vide est une suppression signée coach : séries sans
		// consigne comme textes vides (résumé, exercices). On le dit avant.
		const vides = seriesSansConsigne(modele);
		const textesVides =
			texteOuNull(modele.resume) == null || modele.exercices.some((e) => texteOuNull(e.texte) == null);
		if (vides > 0 || textesVides) {
			const parties = [];
			if (vides > 0)
				parties.push(
					`${vides} ${pluriel(vides, "série sans consigne sera enregistrée comme retirée", "séries sans consigne seront enregistrées comme retirées")} : l'élève n'aura aucune proposition dessus.`
				);
			if (textesVides)
				parties.push(
					vides > 0
						? "Les textes vides (résumé, exercices) seront aussi enregistrés comme retirés."
						: "Les textes vides (résumé, exercices) seront enregistrés comme retirés."
				);
			parties.push("Continuer ?");
			if (!window.confirm(parties.join(" "))) return;
		}
		setEnregistrement(true);
		setErreur(null);
		setMessage(null);
		const res = await saveCoachPreview(studentId, training.id, corpsToutCoach(modele));
		if (!res.success) {
			setEnregistrement(false);
			setErreur(texteRaison(res.raison));
			setConflit(res.raison === "conflit");
			return;
		}
		// Ce que le serveur détient fait foi : on recharge la fiche (séries
		// vides devenues retirées, version, signature coach) plutôt que de
		// deviner localement. Le formulaire reste désactivé jusqu'à la fin.
		setMessage(`Fiche enregistrée (version ${res.version}).${estSoiMeme ? "" : " L'élève est prévenu dans l'app."}`);
		await charger({ garderMessage: true });
		setEnregistrement(false);
	};

	// Mutations locales des champs : immuables, ciblées. Chacune marque la
	// fiche comme modifiée (confirmation à la fermeture).
	const majResume = (texte) => {
		setModele((m) => ({ ...m, resume: texte }));
		setModifie(true);
	};
	const majTexteExo = (idExercice, texte) => {
		setModele((m) => ({
			...m,
			exercices: m.exercices.map((e) => (e.idExercice === idExercice ? { ...e, texte } : e)),
		}));
		setModifie(true);
	};
	const majSerie = (idExercice, ordre, champ, valeur) => {
		setModele((m) => ({
			...m,
			exercices: m.exercices.map((e) =>
				e.idExercice !== idExercice
					? e
					: { ...e, series: e.series.map((s) => (s.ordre === ordre ? { ...s, [champ]: valeur } : s)) }
			),
		}));
		setModifie(true);
	};
	const basculerRetiree = (idExercice, ordre) => {
		setModele((m) => ({
			...m,
			exercices: m.exercices.map((e) =>
				e.idExercice !== idExercice
					? e
					: { ...e, series: e.series.map((s) => (s.ordre === ordre ? { ...s, retiree: !s.retiree } : s)) }
			),
		}));
		setModifie(true);
	};

	const demanderFermeture = () => {
		// Pendant un enregistrement (et le rechargement qui suit), on ne ferme
		// pas : le coach doit voir s'il a abouti.
		if (enregistrement) return;
		if (modifie && !window.confirm("Des consignes ne sont pas enregistrées. Fermer quand même ?")) return;
		onClose();
	};

	// Échap ferme le panneau avec les mêmes garde-fous que la croix. La ref
	// suit la dernière demanderFermeture (état courant) sans réabonner
	// l'écouteur à chaque rendu.
	const fermetureRef = useRef(demanderFermeture);
	fermetureRef.current = demanderFermeture;
	useEffect(() => {
		const surTouche = (e) => {
			if (e.key === "Escape") fermetureRef.current();
		};
		document.addEventListener("keydown", surTouche);
		return () => document.removeEventListener("keydown", surTouche);
	}, []);

	const occupe = remplissage || enregistrement;

	return (
		<div className="modal-overlay" onClick={demanderFermeture}>
			<div className="modal-content consignes-panel" onClick={(e) => e.stopPropagation()}>
				<button className="modal-close" onClick={demanderFermeture} aria-label="Fermer">&times;</button>
				<div className="modal-inner">
					<div className="consignes-entete">
						<h2 className="modal-title" style={{ margin: 0 }}>Consignes et objectifs</h2>
						{modele && modele.personnaliseeParCoach && <span className="consignes-badge">Personnalisée</span>}
						{modele && modele.verrouillee && <span className="consignes-badge consignes-badge--verrou">Séance programmée</span>}
						{modele && modele.version > 0 && <span className="consignes-sous-titre">version {modele.version}</span>}
					</div>
					<p className="consignes-sous-titre">
						{training.nom || "Séance"}
						{training.date ? ` du ${formaterDate(training.date)}` : ""}. Tout ce que vous enregistrez ici est signé
						coach et prime sur les propositions GymNote.
					</p>
					<div className="modal-divider"></div>

					{chargement ? (
						<p className="editor-loading">Chargement…</p>
					) : !modele ? (
						<>
							{/* Enregistrée mais pas relue : le succès reste affiché avec l'erreur. */}
							{message && <p className="consignes-message">{message}</p>}
							<p className="consignes-erreur">{erreur}</p>
							<div className="consignes-actions">
								<button className="modal-btn modal-btn-secondary" style={{ width: "auto" }} onClick={() => charger()}>
									Réessayer
								</button>
							</div>
						</>
					) : (
						<>
							<div className="consignes-corps">
								<div className="consignes-bloc">
									<label className="consignes-label" htmlFor="consignes-resume">Résumé pour l'élève</label>
									<textarea
										id="consignes-resume"
										className="consignes-texte"
										value={modele.resume}
										onChange={(e) => majResume(e.target.value)}
										placeholder="Le mot d'ordre de la séance"
										disabled={occupe}
									/>
								</div>

								{modele.exercices.map((exo) => (
									<div className="consignes-bloc" key={exo.idExercice}>
										<h4>{exo.nom}</h4>
										<label className="consignes-label" htmlFor={`consignes-exo-${exo.idExercice}`}>
											Consigne de l'exercice
										</label>
										<textarea
											id={`consignes-exo-${exo.idExercice}`}
											className="consignes-texte"
											value={exo.texte}
											onChange={(e) => majTexteExo(exo.idExercice, e.target.value)}
											placeholder="Technique, tempo, matériel"
											disabled={occupe}
										/>
										{exo.series.map((s) => {
											const poidsInvalide = estInvalide(exo.idExercice, s.ordre, "poids");
											const repsInvalide = estInvalide(exo.idExercice, s.ordre, "reps");
											const rpeInvalide = estInvalide(exo.idExercice, s.ordre, "rpe");
											const idAide = `consignes-aide-${exo.idExercice}-${s.ordre}`;
										const libelleSerie = `${exo.nom || "Exercice"}, série ${s.ordre + 1}`;
											return (
												<div className="consignes-serie" key={s.ordre}>
													<span className="consignes-serie-num">Série {s.ordre + 1}</span>
													{s.retiree ? (
														<span className="consignes-retiree">Consigne retirée : rien ne sera proposé sur cette série.</span>
													) : (
														<>
															<input
																aria-label={`${libelleSerie} : reps`}
																placeholder="reps"
																inputMode="numeric"
																value={s.reps}
																onChange={(e) => majSerie(exo.idExercice, s.ordre, "reps", e.target.value)}
																disabled={occupe}
																{...attributsInvalide(repsInvalide, idAide)}
															/>
															<input
																aria-label={`${libelleSerie} : poids en kg`}
																placeholder="kg"
																inputMode="decimal"
																value={s.poids}
																onChange={(e) => majSerie(exo.idExercice, s.ordre, "poids", e.target.value)}
																disabled={occupe}
																{...attributsInvalide(poidsInvalide, idAide)}
															/>
															<input
																aria-label={`${libelleSerie} : RPE`}
																placeholder="RPE"
																inputMode="decimal"
																value={s.rpe}
																onChange={(e) => majSerie(exo.idExercice, s.ordre, "rpe", e.target.value)}
																disabled={occupe}
																{...attributsInvalide(rpeInvalide, idAide)}
															/>
															<input
																className="consignes-serie-texte"
																aria-label={`${libelleSerie} : consigne`}
																placeholder="consigne (facultatif)"
																value={s.texte}
																onChange={(e) => majSerie(exo.idExercice, s.ordre, "texte", e.target.value)}
																disabled={occupe}
															/>
														</>
													)}
													<button
														type="button"
														className="consignes-retirer"
														aria-label={`${s.retiree ? "Rétablir" : "Retirer"} la consigne de la série ${s.ordre + 1} de ${exo.nom || "l'exercice"}`}
														onClick={() => basculerRetiree(exo.idExercice, s.ordre)}
														disabled={occupe}
													>
														{s.retiree ? "Rétablir" : "Retirer"}
													</button>
													{(poidsInvalide || repsInvalide || rpeInvalide) && (
														<span className="consignes-erreur-champ" id={idAide}>
															Nombre attendu{rpeInvalide ? " (RPE de 1 à 10)" : ""}. Une fourchette va dans la consigne.
														</span>
													)}
													{!s.retiree && s.repsPrecedentes != null && s.poidsPrecedent != null && (
														<span className="consignes-avant">
															Dernière fois : {s.repsPrecedentes} × {formatWeight(s.poidsPrecedent, 0)} kg
														</span>
													)}
												</div>
											);
										})}
										{exo.series.length === 0 && <p className="consignes-sous-titre">Aucune série dans cette séance.</p>}
									</div>
								))}
							</div>

							{erreur && (
								<p className="consignes-erreur">
									{erreur}
									{conflit && (
										<button
											type="button"
											className="modal-btn modal-btn-secondary"
											style={{ width: "auto", marginLeft: "0.5rem" }}
											onClick={() => charger()}
										>
											Recharger
										</button>
									)}
								</p>
							)}
							{/* Enregistrer est désactivé tant qu'un champ est en rouge : on dit pourquoi. */}
							{!erreur && invalides.length > 0 && <p className="consignes-erreur">{MESSAGE_CHAMPS_INVALIDES}</p>}
							{message && <p className="consignes-message">{message}</p>}

							<div className="consignes-actions">
								<button
									className="modal-btn modal-btn-secondary"
									style={{ width: "auto" }}
									onClick={preremplirAvecGymCoach}
									disabled={occupe}
								>
									{remplissage ? "Gym'Coach calcule…" : "Préremplir avec Gym'Coach"}
								</button>
								<button
									className="modal-btn"
									style={{ width: "auto" }}
									onClick={enregistrer}
									disabled={occupe || invalides.length > 0}
								>
									{enregistrement ? "Enregistrement…" : "Enregistrer"}
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
